import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

interface CustomerAccount {
  id: string;
  name: string;
  contact: string;
  email?: string;
  phone?: string;
  address?: string;
  balance: number;
  creditLimit: number;
  creditUtilization: number;
  status: 'active' | 'suspended' | 'inactive';
  accountType: string;
  createdAt: string;
}

interface Transaction {
  id: string;
  type: 'charge' | 'payment' | 'credit';
  amount: number;
  description: string;
  transactionDate: string;
}

export default function CustomerAccountManager() {
  const [accounts, setAccounts] = useState<CustomerAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<CustomerAccount | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [transactionType, setTransactionType] = useState<'payment' | 'credit'>('payment');

  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    phone: '',
    address: '',
    creditLimit: 5000,
    accountType: 'individual',
  });

  const [transactionData, setTransactionData] = useState({
    amount: 0,
    description: '',
  });

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    try {
      setLoading(true);
      const response = await apiClient.searchAccounts(searchTerm);
      setAccounts(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = async (account: CustomerAccount) => {
    setSelectedAccount(account);
    try {
      const response = await apiClient.getAccountTransactions(account.id);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    }
    setActiveTab('detail');
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await apiClient.createAccount(
        formData.name,
        formData.contact,
        formData.creditLimit,
        formData.accountType as any,
        formData.email,
        formData.phone,
        formData.address
      );
      setShowCreateForm(false);
      setFormData({
        name: '',
        contact: '',
        email: '',
        phone: '',
        address: '',
        creditLimit: 5000,
        accountType: 'individual',
      });
      setSearchTerm('');
      handleSearch();
    } catch (error) {
      console.error('Failed to create account:', error);
      alert('Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount || transactionData.amount <= 0) return;

    try {
      setLoading(true);
      if (transactionType === 'payment') {
        await apiClient.makePayment(selectedAccount.id, transactionData.amount, 'Manual payment');
      } else {
        await apiClient.issueCredit(selectedAccount.id, transactionData.amount, transactionData.description);
      }

      setTransactionData({ amount: 0, description: '' });
      setShowTransactionForm(false);

      // Reload account and transactions
      const accountRes = await apiClient.getAccount(selectedAccount.id);
      setSelectedAccount(accountRes.data);
      const transRes = await apiClient.getAccountTransactions(selectedAccount.id);
      setTransactions(transRes.data);
    } catch (error) {
      console.error('Transaction failed:', error);
      alert('Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: '#28a745',
      suspended: '#ffc107',
      inactive: '#dc3545',
    };
    return colors[status] || '#6c757d';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      active: '✅',
      suspended: '⚠️',
      inactive: '❌',
    };
    return icons[status] || '❓';
  };

  const getCreditColor = (utilization: number) => {
    if (utilization < 50) return '#28a745';
    if (utilization < 80) return '#ffc107';
    return '#dc3545';
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px', fontFamily: 'system-ui' }}>
      <h2>👥 Customer Account Management</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>
        {['search', 'detail'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            disabled={tab === 'detail' && !selectedAccount}
            style={{
              padding: '10px 15px',
              backgroundColor: activeTab === tab ? '#007bff' : '#f0f0f0',
              color: activeTab === tab ? '#fff' : '#666',
              border: 'none',
              borderRadius: '4px',
              cursor: tab === 'detail' && !selectedAccount ? 'not-allowed' : 'pointer',
              fontWeight: activeTab === tab ? 'bold' : 'normal',
              textTransform: 'capitalize',
              opacity: tab === 'detail' && !selectedAccount ? 0.5 : 1,
            }}
          >
            {tab === 'search' && '🔍'} {tab === 'search' && 'Search'}
            {tab === 'detail' && '📄'} {tab === 'detail' && 'Account Detail'}
          </button>
        ))}
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Search by name, phone, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              Search
            </button>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {showCreateForm ? 'Cancel' : 'New Account'}
            </button>
          </div>

          {/* Create Account Form */}
          {showCreateForm && (
            <form onSubmit={handleCreateAccount} style={{ padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '4px', marginBottom: '20px' }}>
              <h3>Create New Account</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label>Contact Person *</label>
                  <input
                    type="text"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label>Credit Limit</label>
                  <input
                    type="number"
                    value={formData.creditLimit}
                    onChange={(e) => setFormData({ ...formData, creditLimit: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label>Account Type</label>
                  <select
                    value={formData.accountType}
                    onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                    <option value="corporate">Corporate</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: '15px',
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Creating...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Search Results */}
          {loading && <p>Loading...</p>}
          {!loading && accounts.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Contact</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Balance</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Credit Limit</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '10px' }}>{account.name}</td>
                      <td style={{ padding: '10px' }}>{account.contact}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>R{account.balance.toFixed(2)}</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>R{account.creditLimit.toFixed(2)}</td>
                      <td style={{ padding: '10px' }}>
                        <span style={{ color: getStatusColor(account.status) }}>
                          {getStatusIcon(account.status)} {account.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px' }}>
                        <button
                          onClick={() => handleSelectAccount(account)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detail Tab */}
      {activeTab === 'detail' && selectedAccount && (
        <div>
          {/* Account Header */}
          <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Account Name</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{selectedAccount.name}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Status</div>
                <div style={{ fontSize: '18px', color: getStatusColor(selectedAccount.status) }}>
                  {getStatusIcon(selectedAccount.status)} {selectedAccount.status}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Outstanding Balance</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: selectedAccount.balance > 0 ? '#dc3545' : '#28a745' }}>
                  R{selectedAccount.balance.toFixed(2)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Credit Limit</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>R{selectedAccount.creditLimit.toFixed(2)}</div>
              </div>
            </div>

            {/* Credit Utilization Bar */}
            <div style={{ marginTop: '15px' }}>
              <div style={{ fontSize: '12px', marginBottom: '5px' }}>
                Credit Utilization: {selectedAccount.creditUtilization.toFixed(1)}%
              </div>
              <div
                style={{
                  height: '10px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '5px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    backgroundColor: getCreditColor(selectedAccount.creditUtilization),
                    width: `${Math.min(100, selectedAccount.creditUtilization)}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <h3>Contact Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
              <div>
                <strong>Contact Person:</strong> {selectedAccount.contact}
              </div>
              {selectedAccount.email && (
                <div>
                  <strong>Email:</strong> {selectedAccount.email}
                </div>
              )}
              {selectedAccount.phone && (
                <div>
                  <strong>Phone:</strong> {selectedAccount.phone}
                </div>
              )}
              {selectedAccount.address && (
                <div>
                  <strong>Address:</strong> {selectedAccount.address}
                </div>
              )}
            </div>
          </div>

          {/* Transaction Actions */}
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setShowTransactionForm(!showTransactionForm)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#ffc107',
                color: '#000',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px',
              }}
            >
              {showTransactionForm ? 'Cancel' : 'Add Transaction'}
            </button>
          </div>

          {showTransactionForm && (
            <form onSubmit={handleTransaction} style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '4px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label>Transaction Type</label>
                  <select
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value as any)}
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="payment">Payment</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                <div>
                  <label>Amount (R)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={transactionData.amount}
                    onChange={(e) => setTransactionData({ ...transactionData, amount: parseFloat(e.target.value) })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '10px' }}>
                <label>Description/Reason</label>
                <textarea
                  value={transactionData.description}
                  onChange={(e) => setTransactionData({ ...transactionData, description: e.target.value })}
                  style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '60px' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: '10px',
                  padding: '8px 15px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Processing...' : 'Record Transaction'}
              </button>
            </form>
          )}

          {/* Transactions List */}
          <div>
            <h3>Recent Transactions</h3>
            {transactions.length === 0 ? (
              <p style={{ color: '#666' }}>No transactions yet</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Date</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Description</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.slice(0, 20).map((tx) => (
                      <tr key={tx.id} style={{ borderBottom: '1px solid #ddd' }}>
                        <td style={{ padding: '10px' }}>
                          {new Date(tx.transactionDate).toLocaleDateString('en-ZA')}
                        </td>
                        <td style={{ padding: '10px', textTransform: 'capitalize' }}>
                          {tx.type === 'charge' && '💳'} {tx.type === 'payment' && '💰'} {tx.type === 'credit' && '✅'} {tx.type}
                        </td>
                        <td style={{ padding: '10px' }}>{tx.description}</td>
                        <td
                          style={{
                            padding: '10px',
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: tx.type === 'charge' ? '#dc3545' : '#28a745',
                          }}
                        >
                          {tx.type === 'charge' ? '+' : '-'}R{tx.amount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
